/**
 * The ink outlines of a wall leaf, and the height-span arithmetic they share
 * with the leaf's slab. Outlines follow what is visible: a cutout's edges are
 * drawn only where it stops short of its leaf's edges, the inner floor and
 * ceiling lines break where a cutout reaches them, and a square end's crease
 * is drawn only over the heights where the end is exposed.
 */

import { type Vec3 } from '../math3d';
import { reachesEnd, reachesStart, solidSpans, type WallCutout } from '../room-geometry';
import { type MeshBuilder } from './mesh3d';

/** A height interval [y0, y1]. */
export type Span = [number, number];

/** The world point `s` along a leaf, `side` slab thicknesses across it (0 the inner face, 1 the outer), at `height`. */
export type SlabPoint = (s: number, side: number, height: number) => Vec3;

export const reachesFloor = (cutout: WallCutout): boolean => cutout.base <= 1e-9;
export const reachesCeiling = (cutout: WallCutout, height: number): boolean => cutout.top >= height - 1e-9;
export const heightSpan = (cutout: WallCutout): Span => [cutout.base, cutout.top];

/** `spans` with `holes` cut out of them, dropping remainders too thin to see. */
export function subtractSpans(spans: Span[], holes: Span[]): Span[] {
  return holes.reduce<Span[]>((remaining, [holeBottom, holeTop]) =>
    remaining.flatMap(([bottom, top]) => {
      const parts: Span[] = [];
      if (bottom < holeBottom) parts.push([bottom, Math.min(top, holeBottom)]);
      if (top > holeTop) parts.push([Math.max(bottom, holeTop), top]);
      return parts.filter(([y0, y1]) => y1 - y0 > 1e-4);
    }), spans);
}

/** The overlaps of two span lists. */
export function intersectSpans(first: Span[], second: Span[]): Span[] {
  const overlaps: Span[] = [];
  for (const [firstBottom, firstTop] of first) {
    for (const [secondBottom, secondTop] of second) {
      const y0 = Math.max(firstBottom, secondBottom), y1 = Math.min(firstTop, secondTop);
      if (y1 - y0 > 1e-4) overlaps.push([y0, y1]);
    }
  }
  return overlaps;
}

/**
 * A cutout's outline on the inner face over its slab span [start, end]: a
 * sill and a lintel edge only where it stops short of the floor and the
 * ceiling, and a jamb edge only where it stops short of the leaf's end (one
 * reaching the end has no jamb of its own; the neighbor's square end draws
 * there).
 */
export function outlineHole(
  meshBuilder: MeshBuilder,
  slabPoint: SlabPoint,
  cutout: WallCutout,
  start: number,
  end: number,
  length: number,
  height: number,
): void {
  const { base, top } = cutout;
  if (!reachesFloor(cutout)) meshBuilder.line(slabPoint(start, 0, base), slabPoint(end, 0, base));
  if (!reachesCeiling(cutout, height)) meshBuilder.line(slabPoint(start, 0, top), slabPoint(end, 0, top));
  if (!reachesStart(cutout)) meshBuilder.line(slabPoint(start, 0, base), slabPoint(start, 0, top));
  if (!reachesEnd(cutout, length)) meshBuilder.line(slabPoint(end, 0, base), slabPoint(end, 0, top));
}

/** The inner floor and ceiling lines, each over the spans where the wall actually meets that slab: broken by cutouts touching the floor or ceiling. */
export function outlineRuns(meshBuilder: MeshBuilder, slabPoint: SlabPoint, length: number, height: number, cutouts: WallCutout[]): void {
  for (const [start, end] of solidSpans(length, cutouts, reachesFloor)) meshBuilder.line(slabPoint(start, 0, 0), slabPoint(end, 0, 0));
  for (const [start, end] of solidSpans(length, cutouts, cutout => reachesCeiling(cutout, height))) {
    meshBuilder.line(slabPoint(start, 0, height), slabPoint(end, 0, height));
  }
}

/**
 * The outline of one square end over the heights `exposed`: the crease
 * between the inner face and the end cap at the slab's end, and, where the
 * exposure reaches the floor or the ceiling and the leaf is solid there,
 * the stub's inner edge along that slab, from the inner corner out to the end.
 */
export function outlineEnd(
  meshBuilder: MeshBuilder,
  slabPoint: SlabPoint,
  corner: number,
  end: number,
  exposed: Span[],
  solidAtFloor: boolean,
  solidAtCeiling: boolean,
  height: number,
): void {
  for (const [y0, y1] of exposed) meshBuilder.line(slabPoint(end, 0, y0), slabPoint(end, 0, y1));
  if (Math.abs(end - corner) < 1e-9) return;
  if (solidAtFloor && exposed.some(([y0]) => y0 <= 1e-9)) meshBuilder.line(slabPoint(corner, 0, 0), slabPoint(end, 0, 0));
  if (solidAtCeiling && exposed.some(([, y1]) => y1 >= height - 1e-9)) meshBuilder.line(slabPoint(corner, 0, height), slabPoint(end, 0, height));
}
